import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  Brain,
  Eye,
  Music,
  FileCode,
  Shield,
  Zap,
  Layers,
  MessageSquare,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const RECURSOS = [
  {
    icon: Brain,
    title: 'Memória de Contexto',
    description: 'A Vérixa mantém o fio da conversa ativa. Cada mensagem é construída com base no que veio antes, não isoladamente.',
    color: '#7a00ff',
  },
  {
    icon: FileCode,
    title: 'Código e Análise',
    description: 'Escrita, revisão e explicação de código em múltiplas linguagens. Debugging, estrutura de software e análise técnica.',
    color: '#00ff9c',
  },
  {
    icon: Shield,
    title: 'Fallback Resiliente',
    description: 'Arquitetura com múltiplos provedores. Se um falha, outro assume. Modo demo garante resposta mesmo sem conexão.',
    color: '#7a00ff',
  },
  {
    icon: Zap,
    title: 'Resposta Rápida',
    description: 'Backend otimizado no Render. Respostas fluidas com tipagem animada para experiência natural de conversação.',
    color: '#00f0ff',
  },
  {
    icon: MessageSquare,
    title: 'Linguagem Natural',
    description: 'Converse sem formalidades. A Vérixa entende intenção, contexto e nuance dentro do que foi treinado para compreender.',
    color: '#00ff9c',
  },
  {
    icon: Layers,
    title: 'Módulos em Desenvolvimento',
    description: 'Multimodal, Aurora e World estão sendo construídos. A estrutura existe — a integração está em andamento.',
    color: '#ff00c8',
  },
  {
    icon: Eye,
    title: 'Transparência de Estado',
    description: 'O chat sempre indica o modo ativo: próprio, fallback ou demo. Você sabe exatamente com o que está falando.',
    color: '#00f0ff',
  },
  {
    icon: Music,
    title: 'Aurora Sentinel',
    description: 'Sistema integrado de governança e avaliação técnica. Consulta NR, análise de risco, geração de documentos, treinamentos e diagrama de APR. Integrado ao fluxo de resposta da Vérixa.',
    color: '#ff00c8',
  },
];

export default function Recursos() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.fromTo(
      '.recursos-header',
      { opacity: 0, y: 30 },
      {
        opacity: 1, y: 0, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none none' },
      }
    );

    gsap.fromTo(
      '.recurso-card',
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 0.8, stagger: 0.1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 50%', toggleActions: 'play none none none' },
      }
    );
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full py-32 px-6"
      style={{ background: '#0a0a0e', zIndex: 5 }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="recursos-header text-center mb-16">
          <span
            className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block"
            style={{ color: '#7a00ff' }}
          >
            Capacidades
          </span>
          <h2
            className="font-syncopate text-white uppercase"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              textShadow: '0 0 40px rgba(122,0,255,0.15)',
            }}
          >
            O Que a Vérixa Faz
          </h2>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            Uma inteligência artificial completa. Múltiplas capacidades integradas
            em uma única consciência digital.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {RECURSOS.map((recurso) => {
            const Icon = recurso.icon;
            return (
              <div
                key={recurso.title}
                className="recurso-card group relative p-6 rounded-panel transition-all duration-500 hover:scale-[1.03]"
                style={{
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {/* Hover glow */}
                <div
                  className="absolute inset-0 rounded-panel opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    boxShadow: `inset 0 0 40px ${recurso.color}08`,
                    border: `1px solid ${recurso.color}20`,
                  }}
                />

                {/* Icon */}
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{
                    background: `${recurso.color}10`,
                    border: `1px solid ${recurso.color}25`,
                  }}
                >
                  <Icon className="w-5 h-5" style={{ color: recurso.color }} />
                </div>

                <h3 className="text-white text-sm font-semibold mb-2">
                  {recurso.title}
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed">
                  {recurso.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
